/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useCallback, useRef } from "react";
import { isWindowDefined } from "@huub-web/utils";

import { TrackingContext } from "../../contexts/TrackingContext";
import { useDebug } from "../../hooks/useDebug";
import type {
  TrackingEventType,
  TrackingEventData,
} from "../../types/TrackingEventTypes";
import type { ChildrenProp } from "../../props";

export type TrackerFunction = <T extends TrackingEventType>(
  event: T,
  data?: TrackingEventData<T>,
) => void;

export type TrackingProviderProps = ChildrenProp & {
  trackers?: Record<string, TrackerFunction>;
};

/**
 * A provider that allows tracking events to be sent to multiple trackers.
 * Each tracker is a function that receives (event, data) and handles it appropriately.
 *
 * Uses refs for `trackers` and `log` so the `track` function is referentially
 * stable. This prevents downstream consumers (e.g. RouteChangeTracker) from
 * re-firing effects when the parent tree re-renders.
 */
export function TrackingProvider({
  trackers = {
    gtm: (data) => {
      if (isWindowDefined()) {
        (window as any).dataLayer?.push(data);
      }
    },
  },
  children,
}: TrackingProviderProps) {
  const { log } = useDebug();

  const trackersRef = useRef(trackers);
  trackersRef.current = trackers;

  const logRef = useRef(log);
  logRef.current = log;

  const track = useCallback(function track(
    event: TrackingEventType,
    data?: TrackingEventData,
  ) {
    logRef.current("magenta", "TrackingProvider", `Tracking '${event}'`, data);

    if (isWindowDefined()) {
      // Send event to all configured trackers
      Object.entries(trackersRef.current).forEach(([name, tracker]) => {
        try {
          tracker(event, data);
        } catch (error) {
          console.error(`[TrackingProvider] Error in ${name} tracker:`, error);
        }
      });
    }
  }, []);

  return (
    <TrackingContext.Provider value={{ track }}>
      {children}
    </TrackingContext.Provider>
  );
}

// function getDataForPage(): PageData {
//   if (!window) {
//     return {
//       host: "",
//       path: "",
//       title: "",
//     };
//   }

//   return {
//     host: window.location.hostname,
//     path: window.location.pathname,
//     title: window.document.title,
//   };
// }

// function getDataForElement(element: Element): ElementData {
//   if (element instanceof HTMLAnchorElement) {
//     return anchorElementData(element);
//   } else if (element instanceof HTMLImageElement) {
//     return imageElementData(element);
//   } else if (element instanceof HTMLMediaElement) {
//     return mediaElementData(element);
//   } else if (element instanceof HTMLIFrameElement) {
//     return frameElementData(element);
//   }
//   return elementData(element);
// }

// function elementData(element: Element): BaseElementData {
//   return {
//     tag: element.tagName.toLowerCase(),
//     id: element.id || null,
//     path: cssPath(element),
//     label:
//       (element instanceof HTMLElement ? element.title : undefined) ||
//       element.ariaLabel ||
//       null,
//     text: element.textContent || null,
//   };
// }

// function anchorElementData(element: HTMLAnchorElement): AnchorElementData {
//   return {
//     ...elementData(element),
//     anchor: {
//       destination: element.href,
//     },
//   };
// }

// function imageElementData(element: HTMLImageElement): ImageElementData {
//   return {
//     ...elementData(element),
//     image: {
//       source: element.srcset || element.src,
//       description: element.alt,
//     },
//   };
// }

// function mediaElementData(element: HTMLMediaElement): MediaElementData {
//   return {
//     ...elementData(element),
//     media: {
//       source: element.currentSrc,
//       duration: element.duration,
//       time: element.currentTime,
//       progress: element.currentTime / element.duration,
//     },
//   };
// }

// function frameElementData(element: HTMLIFrameElement): FrameElementData {
//   return {
//     ...elementData(element),
//     frame: {
//       source: element.src,
//     },
//   };
// }

// function cssPath(element: Element) {
//   const path = [];

//   let node: Node | null = element;

//   while (node instanceof Element) {
//     let selector = node.nodeName.toLowerCase();

//     if (node.id) {
//       selector += "#" + node.id;
//       path.unshift(selector);
//       break;
//     }

//     let sibling: Element | null = node;
//     let n = 1;

//     while ((sibling = sibling.previousElementSibling)) {
//       if (sibling.nodeName.toLowerCase() === selector) {
//         n++;
//       }
//     }

//     if (n !== 1) {
//       selector += `:nth-of-type(${n})`;
//     }

//     path.unshift(selector);
//     node = node.parentNode;
//   }

//   return path.join(" > ");
// }
